import * as React from 'react';
import { createContext, useContext, useState } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';

type CoachChatConfig = Omit<AiCoachRespondToUserMessageInput, 'message'> & { message?: undefined };

type CoachStateProviderType = {
  coachOpen: boolean;
  setCoachOpen: React.Dispatch<React.SetStateAction<boolean>>;
  contextConfig: CoachChatConfig;
  setContextConfig: React.Dispatch<React.SetStateAction<CoachChatConfig>>;
  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  setRenderCustomMessage: React.Dispatch<
    React.SetStateAction<((props: ChatMessageProps) => React.ReactElement | null) | undefined>
  >;
  isMinimizedChat: boolean;
  toggleFullScreen: () => void;
};
const defaultCoachState: CoachStateProviderType = {
  coachOpen: false,
  setCoachOpen: () => {},
  contextConfig: {
    config: {
      coachActionId: '',
      coachModeId: 'BASIC',
    },
  },
  setContextConfig: () => {},
  renderCustomMessage: undefined,
  setRenderCustomMessage: () => {},
  isMinimizedChat: false,
  toggleFullScreen: () => {},
};

const CoachStateContext = createContext(defaultCoachState);

/**
 * @description This hook is used to access the coach state such as open/close state and context config.
 */
export const useCoachState = () => {
  const context = useContext(CoachStateContext);
  return context;
};

/**
 * @description This provider is used to manage the state of Coach.
 */
export const CoachStateProvider = ({ children }: { children: React.ReactNode }) => {
  const [coachOpen, setCoachOpen] = useState(defaultCoachState.coachOpen);
  const [contextConfig, setContextConfig] = useState(defaultCoachState.contextConfig);
  const [isMinimizedChat, setIsMinimizedChat] = useState(defaultCoachState.isMinimizedChat);
  const [renderCustomMessage, setRenderCustomMessage] = useState<
    ((props: ChatMessageProps) => React.ReactElement | null) | undefined
  >(defaultCoachState.renderCustomMessage);

  return (
    <CoachStateContext.Provider
      value={{
        coachOpen,
        setCoachOpen,
        contextConfig,
        setContextConfig: (...data) => {
          setContextConfig(...data);
        },
        renderCustomMessage,
        setRenderCustomMessage,
        isMinimizedChat,
        toggleFullScreen: () => setIsMinimizedChat((prev) => !prev),
      }}
    >
      {children}
    </CoachStateContext.Provider>
  );
};
